import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { organizationService, OrgInvitation } from '../services/organizationService';
import { useAuth } from '../contexts/AuthContext';

const GOOGLE_REDIRECT = `${window.location.origin}/auth/callback`;

type PageState = 'loading' | 'invalid' | 'expired' | 'already_accepted' | 'ready' | 'existing_user' | 'done';

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshOrg } = useAuth();

  const [pageState, setPageState]       = useState<PageState>('loading');
  const [invitation, setInvitation]     = useState<OrgInvitation | null>(null);
  const [orgName, setOrgName]           = useState('');
  const [fullName, setFullName]         = useState('');
  const [password, setPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError]       = useState('');
  const [submitting, setSubmitting]     = useState(false);

  useEffect(() => {
    if (!token) { setPageState('invalid'); return; }
    (async () => {
      const inv = await organizationService.getInvitationByToken(token);
      if (!inv) { setPageState('invalid'); return; }
      if (inv.status === 'accepted') { setPageState('already_accepted'); return; }
      if (inv.status === 'revoked')  { setPageState('invalid'); return; }
      if (new Date(inv.expires_at) < new Date()) { setPageState('expired'); return; }
      setInvitation(inv);
      if (inv.metadata?.name) setFullName(inv.metadata.name);
      const { data } = await supabase.from('organizations').select('name').eq('id', inv.org_id).single();
      setOrgName(data?.name ?? '');
      const { data: { user } } = await supabase.auth.getUser();
      setPageState(user ? 'existing_user' : 'ready');
    })();
  }, [token]);

  // Existing signed-in user accepts with one click
  const handleExistingUserAccept = async () => {
    if (!invitation) return;
    setSubmitting(true);
    setFormError('');
    try {
      await organizationService.acceptInvitation(invitation.token);
      await refreshOrg();
      setPageState('done');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (e: any) {
      setFormError(e.message || 'Failed to accept invitation');
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!invitation || !token) return;
    setFormError('');
    localStorage.setItem('pendingInviteToken', token);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: GOOGLE_REDIRECT },
    });
    if (error) {
      localStorage.removeItem('pendingInviteToken');
      setFormError(error.message);
    }
  };

  // New user: collect name + password, sign up, then accept invite
  const handleJoinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!invitation || !fullName.trim()) return;
    if (password !== confirmPassword) { setFormError('Passwords do not match'); return; }
    if (password.length < 6) { setFormError('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    const pwd = password;
    // Clear passwords from state immediately before async calls
    setPassword('');
    setConfirmPassword('');
    try {
      localStorage.setItem('pendingInviteToken', token!);

      const { data, error } = await supabase.auth.signUp({
        email: invitation.email,
        password: pwd,
        options: { data: { full_name: fullName.trim() } },
      });
      if (error) throw error;
      if (!data.user) throw new Error('Signup failed — please try again');
      if (!data.session) {
        localStorage.removeItem('pendingInviteToken');
        setFormError('Please check your email and confirm your account, then click the invite link again to join.');
        setSubmitting(false);
        return;
      }

      await organizationService.acceptInvitation(invitation.token);
      localStorage.removeItem('pendingInviteToken');
      await refreshOrg();
      setPageState('done');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (e: any) {
      localStorage.removeItem('pendingInviteToken');
      setFormError(e.message || 'Something went wrong');
      setSubmitting(false);
    }
  };

  // ── Shell (centered card) ────────────────────────────────────
  const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={s.page}><div style={s.card}>{children}</div></div>
  );

  if (pageState === 'loading') return (
    <Shell>
      <div style={s.center}>
        <div style={s.spinner} />
        <p style={s.hint}>Validating your invitation…</p>
      </div>
    </Shell>
  );

  if (pageState === 'invalid') return (
    <Shell>
      <div style={s.center}>
        <div style={{ ...s.iconCircle, background: '#FEE2E2' }}><span style={{ fontSize: 32 }}>✕</span></div>
        <h2 style={{ ...s.title, color: '#DC2626' }}>Invalid Invitation</h2>
        <p style={s.subtitle}>This invitation link is invalid or has been revoked.</p>
        <p style={s.hint}>Contact your workspace administrator for a new invite.</p>
      </div>
    </Shell>
  );

  if (pageState === 'expired') return (
    <Shell>
      <div style={s.center}>
        <div style={{ ...s.iconCircle, background: '#ECEAFF' }}><span style={{ fontSize: 32 }}>⏰</span></div>
        <h2 style={{ ...s.title, color: '#D97706' }}>Invitation Expired</h2>
        <p style={s.subtitle}>This link has expired (valid for 7 days).</p>
        <p style={s.hint}>Ask your admin to send a new invitation.</p>
      </div>
    </Shell>
  );

  if (pageState === 'already_accepted') return (
    <Shell>
      <div style={s.center}>
        <div style={{ ...s.iconCircle, background: '#DCFCE7' }}><span style={{ fontSize: 32 }}>✓</span></div>
        <h2 style={{ ...s.title, color: '#16A34A' }}>Already Joined</h2>
        <p style={s.subtitle}>This invitation has already been accepted.</p>
        <button style={s.primaryBtn} onClick={() => navigate('/login')}>Go to Login</button>
      </div>
    </Shell>
  );

  if (pageState === 'done') return (
    <Shell>
      <div style={s.center}>
        <div style={{ ...s.iconCircle, background: '#DCFCE7' }}><span style={{ fontSize: 40 }}>🎉</span></div>
        <h2 style={{ ...s.title, color: '#16A34A' }}>Welcome aboard!</h2>
        <p style={s.subtitle}>You've joined <strong>{orgName}</strong>.</p>
        <p style={s.hint}>Redirecting to your dashboard…</p>
        <div style={s.spinner} />
      </div>
    </Shell>
  );

  // ── Existing user ────────────────────────────────────────────
  if (pageState === 'existing_user') return (
    <Shell>
      <div style={s.logoRow}>
        <div style={s.orgAvatar}>{orgName.charAt(0).toUpperCase()}</div>
        <span style={s.orgLabel}>{orgName}</span>
      </div>
      <h2 style={s.title}>You've been invited</h2>
      <p style={s.subtitle}>
        You're already signed in. Click below to join <strong>{orgName}</strong> as <strong>{invitation?.role_name ?? invitation?.role}</strong>.
      </p>
      {formError && <div style={s.error}>{formError}</div>}
      <button style={{ ...s.primaryBtn, marginTop: 20 }} onClick={handleExistingUserAccept} disabled={submitting}>
        {submitting ? 'Joining…' : `Accept & Join ${orgName}`}
      </button>
    </Shell>
  );

  // ── Ready — new user join form ───────────────────────────────
  return (
    <div style={s.splitPage}>
      <div style={s.leftPanel}>
        <div style={s.leftContent}>
          <div style={s.orgAvatarLarge}>{orgName.charAt(0).toUpperCase()}</div>
          <h1 style={s.leftTitle}>You're invited to join</h1>
          <h2 style={s.leftOrg}>{orgName}</h2>
          {invitation && (
            <p style={{ ...s.leftDesc, marginBottom: 8 }}>
              Role: <strong style={{ color: '#fff' }}>{invitation.role_name ?? invitation.role}</strong>
            </p>
          )}
          <p style={s.leftDesc}>
            Create your account to start collaborating with your team on task boards, whiteboards, documents, and more.
          </p>
          <div style={s.leftFeatures}>
            {['📋 Task Boards', '🎨 Whiteboards', '📄 Docs Writer', '📅 Meeting Plans'].map(f => (
              <div key={f} style={s.featureItem}>{f}</div>
            ))}
          </div>
        </div>
      </div>

      <div style={s.rightPanel}>
        <div style={s.rightContent}>
          <h2 style={s.formTitle}>Create your account</h2>
          <p style={s.formSubtitle}>Joining as <strong>{invitation?.email}</strong></p>

          <button style={s.googleBtn} type="button" onClick={handleGoogleSignIn}>
            <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>

          <div style={s.divider}><span style={s.dividerText}>or set a password</span></div>

          <form onSubmit={handleJoinSubmit} style={s.form}>
            {formError && <div style={s.error}>{formError}</div>}
            <div style={s.field}>
              <label style={s.label}>Full Name <span style={s.req}>*</span></label>
              <input
                style={invitation?.metadata?.name ? { ...s.input, opacity: 0.7, cursor: 'not-allowed' } : s.input}
                type="text" placeholder="Your full name"
                value={fullName} onChange={e => setFullName(e.target.value)}
                required autoFocus={!invitation?.metadata?.name}
                disabled={submitting || !!invitation?.metadata?.name}
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Email</label>
              <input style={{ ...s.input, opacity: 0.6, cursor: 'not-allowed' }} type="email" value={invitation?.email || ''} disabled />
            </div>
            <div style={s.field}>
              <label style={s.label}>Password <span style={s.req}>*</span></label>
              <div style={s.passwordWrap}>
                <input
                  style={{ ...s.input, paddingRight: 44 }}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 6 characters"
                  value={password} onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required disabled={submitting}
                />
                <button type="button" style={s.eyeBtn} onClick={() => setShowPassword(p => !p)}>
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Confirm Password <span style={s.req}>*</span></label>
              <input
                style={s.input} type="password" placeholder="Repeat your password"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required disabled={submitting}
              />
            </div>
            <button type="submit" style={s.primaryBtn} disabled={submitting || !fullName.trim()}>
              {submitting ? <><span style={s.btnSpinner} /> Setting up account…</> : `Accept & Join ${orgName}`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ── Styles ───────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    background: 'linear-gradient(135deg, #F5E6D3 0%, #E8D4B8 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    background: '#fff', borderRadius: 20, width: '100%', maxWidth: 480,
    padding: '40px 40px 36px', boxShadow: '0 12px 40px rgba(139,92,46,0.15)',
  },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 },
  iconCircle: {
    width: 72, height: 72, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8,
  },
  title:    { margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: '#1a1a1a' },
  subtitle: { margin: 0, fontSize: 15, color: '#555', lineHeight: 1.6 },
  hint:     { margin: '4px 0 0', fontSize: 13, color: '#999', lineHeight: 1.5 },
  primaryBtn: {
    width: '100%', padding: '13px 0', marginTop: 8,
    background: '#8B5C2E', color: '#fff', border: 'none',
    borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  spinner: {
    width: 32, height: 32, border: '3px solid #E8D4B8', borderTopColor: '#8B5C2E',
    borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '16px auto 0',
  },
  logoRow:  { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  orgAvatar: {
    width: 44, height: 44, background: '#8B5C2E', color: '#fff',
    borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 20, fontWeight: 700,
  },
  orgLabel: { fontSize: 16, fontWeight: 600, color: '#1a1a1a' },
  error: {
    background: '#FFF0F0', border: '1px solid #FFB3B3', color: '#C00',
    borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 4,
  },
  splitPage: { minHeight: '100vh', display: 'flex', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  leftPanel: {
    flex: 1, background: 'linear-gradient(160deg, #2D1A0E 0%, #5C3317 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 48px',
  },
  leftContent:    { maxWidth: 400, color: '#F5E6D3' },
  orgAvatarLarge: {
    width: 64, height: 64, background: 'rgba(245,230,211,0.2)',
    borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 28, fontWeight: 700, color: '#F5E6D3', marginBottom: 28,
  },
  leftTitle: { margin: '0 0 4px', fontSize: 28, fontWeight: 700, color: '#F5E6D3' },
  leftOrg:   { margin: '0 0 20px', fontSize: 32, fontWeight: 800, color: '#fff' },
  leftDesc:  { margin: '0 0 32px', fontSize: 15, color: 'rgba(245,230,211,0.75)', lineHeight: 1.7 },
  leftFeatures: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  featureItem:  { padding: '6px 14px', background: 'rgba(245,230,211,0.12)', borderRadius: 20, fontSize: 13, color: 'rgba(245,230,211,0.9)', fontWeight: 500 },
  rightPanel: {
    width: 480, flexShrink: 0, background: '#FDFAF7',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 40px', overflowY: 'auto',
  },
  rightContent: { width: '100%', maxWidth: 380 },
  formTitle:    { margin: '0 0 4px', fontSize: 24, fontWeight: 700, color: '#1a1a1a' },
  formSubtitle: { margin: '0 0 24px', fontSize: 14, color: '#888' },
  googleBtn: {
    width: '100%', padding: '11px 0', background: '#fff',
    border: '1.5px solid #E0D0C0', borderRadius: 10, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, color: '#333', marginBottom: 20,
  },
  divider:     { position: 'relative', textAlign: 'center', marginBottom: 20, borderTop: '1px solid #E8D4B8' },
  dividerText: { position: 'relative', top: -10, background: '#FDFAF7', padding: '0 12px', fontSize: 12, color: '#aaa' },
  form:  { display: 'flex', flexDirection: 'column', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 13, fontWeight: 600, color: '#444' },
  req:   { color: '#C00' },
  input: { padding: '10px 14px', borderRadius: 8, border: '1.5px solid #E0D0C0', fontSize: 14, outline: 'none', background: '#fff', width: '100%', boxSizing: 'border-box' },
  passwordWrap: { position: 'relative' },
  eyeBtn: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 4 },
  btnSpinner: { width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' },
};

if (typeof document !== 'undefined' && !document.getElementById('ai-spin')) {
  const style = document.createElement('style');
  style.id = 'ai-spin';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

export default AcceptInvite;
